import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { FolderPlus, CheckCircle, X } from "lucide-react";

/**
 * Attach a resource (material/machine/inventory) to a project.
 * Backend contract unchanged: POST /projects/{pid}/attach + /detach
 *
 * UX: select dropdown + Attach button. When already attached:
 *   - dropdown is highlighted (moss/green) with a "Linked" badge + checkmark
 *   - clear toast feedback on every success/error
 *   - Detach button labelled "Remove link" for clarity
 */
export default function AttachToProject({ objectType, objectId, currentProjectId, onChange }) {
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState(currentProjectId || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => { setPid(currentProjectId || ""); }, [currentProjectId]);

  const linkedProject = currentProjectId
    ? projects.find((p) => p.id === currentProjectId)
    : null;

  const attach = async () => {
    if (!pid) return;
    setBusy(true);
    try {
      await api.post(`/projects/${pid}/attach`, { object_type: objectType, object_id: objectId });
      const proj = projects.find((p) => p.id === pid);
      toast.success(t("prj.attachedTo").replace("{name}", proj?.namn || ""));
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setBusy(false); }
  };

  const detach = async () => {
    if (!currentProjectId) return;
    setBusy(true);
    try {
      await api.post(`/projects/${currentProjectId}/detach`, { object_type: objectType, object_id: objectId });
      toast.success(t("prj.detached"));
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setBusy(false); }
  };

  const isLinked = !!currentProjectId;
  const canAttach = pid && pid !== currentProjectId;

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-4" data-testid="attach-to-project">
      <div className="label-tech mb-2 inline-flex items-center gap-1">
        <FolderPlus size={12} /> {t("prj.attach")}
      </div>

      {/* Linked indicator — clear visual confirmation when a project IS attached */}
      {isLinked && (
        <div
          className="mb-3 px-3 py-2 border-2 flex items-center gap-2"
          style={{ borderColor: "var(--moss)", background: "color-mix(in srgb, var(--moss) 12%, var(--surface))" }}
          data-testid="attach-linked-indicator"
        >
          <CheckCircle size={16} style={{ color: "var(--moss)" }} />
          <div className="text-sm flex-1 min-w-0">
            <span className="font-semibold">{t("prj.linkedTo")}: </span>
            <span>{linkedProject?.namn || t("prj.unknownProject")}</span>
          </div>
          <span
            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"
            style={{ background: "var(--moss)", color: "var(--bone)" }}
          >
            {t("prj.linkedBadge")}
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <select
            className="input-tech"
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            data-testid="attach-project-select"
            style={isLinked ? { borderColor: "var(--moss)" } : undefined}
          >
            <option value="">{t("prj.pickProject")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id === currentProjectId ? `✓ ${p.namn}` : p.namn}
              </option>
            ))}
          </select>
          {projects.length === 0 && (
            <div className="text-xs text-[var(--ink-500)] mt-1">{t("prj.noneYet")}</div>
          )}
        </div>
        <button
          className="btn-primary"
          onClick={attach}
          disabled={!canAttach || busy}
          data-testid="attach-btn"
        >
          {busy ? "…" : (isLinked ? t("prj.changeLink") : t("prj.attach"))}
        </button>
        {isLinked && (
          <button
            className="btn-ghost text-[var(--brick)] inline-flex items-center gap-1"
            onClick={detach}
            disabled={busy}
            data-testid="detach-btn"
          >
            <X size={12} /> {t("prj.removeLink")}
          </button>
        )}
      </div>
    </div>
  );
}
